import classNames from 'classnames';
import { DateTime } from 'luxon';
import { DocumentTextIcon, SparklesIcon } from '@heroicons/react/outline';

import { ActivityType } from '../../../../generated/graphql';
import {
  AnalyticsComponent,
  AnalyticsPage,
  ProviderAnalyticsEvent,
  trackProviderEvent,
} from '../../../../lib/analytics';
import FollowUpTemplateCardActionButton from './FollowUpTemplateCardActionButton';
import VoiceNoteLabel from '../../../components/VoiceNoteLabel';
import ActivityTypeBadges, {
  TotalAttachmentsCountBadge,
} from '../../../components/ActivityAttachmentBadges';
import Spinner from '../../../svgs/Spinner';

export type FollowUpTemplateCardData = {
  id: string;
  name?: string | null;
  subject: string;
  providerNote: string;
  createdAt: string;
  updatedAt: string;
  providerVoiceNote?: { duration: number } | null;
  activityTemplates: Array<{ activityType: ActivityType }>;
};

const FollowUpTemplateCard = <T extends FollowUpTemplateCardData>({
  followUpTemplate,
  isUsedInAutomation = false,
  showActions = true,
  disabled = false,
  isApplying = false,
  onApplyFollowUpTemplate,
  onRenameFollowUpTemplate,
  onDuplicateFollowUpTemplate,
  onDeleteFollowUpTemplate,
  renameActionLabel,
  className,
}: {
  followUpTemplate: T;
  isUsedInAutomation?: boolean;
  showActions?: boolean;
  disabled?: boolean;
  isApplying?: boolean;
  onApplyFollowUpTemplate: (followUpTemplate: T) => Promise<void>;
  onRenameFollowUpTemplate?: (followUpTemplate: T) => Promise<void>;
  onDuplicateFollowUpTemplate?: (followUpTemplate: T) => Promise<void>;
  onDeleteFollowUpTemplate?: (followUpTemplate: T) => Promise<void>;
  renameActionLabel?: string;
  className?: string;
}) => {
  const handleCardClick = () => {
    if (disabled || isApplying) return;
    trackProviderEvent(ProviderAnalyticsEvent.MailTemplateApplied, {
      originPage: AnalyticsPage.FollowUpBuilder,
      originComponent: AnalyticsComponent.FollowUpDrawer,
      mailTemplateType: 'template',
    });
    void onApplyFollowUpTemplate(followUpTemplate);
  };

  const hasVoiceNote = !!followUpTemplate.providerVoiceNote;
  const hasBodyMessage = followUpTemplate.providerNote.trim().length > 0;

  const dateFormat = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  } as const;

  const createdDateTime = DateTime.fromISO(followUpTemplate.createdAt);
  const updatedDateTime = DateTime.fromISO(followUpTemplate.updatedAt);

  const createdTime = createdDateTime.toLocaleString(dateFormat);
  const updatedTime = updatedDateTime.toLocaleString(dateFormat);

  // Consider it "updated" only if the difference is more than 1 second
  const wasUpdated =
    Math.abs(updatedDateTime.diff(createdDateTime, 'seconds').seconds) > 1;

  return (
    <div
      className={classNames(
        'relative flex flex-col gap-y-3 rounded-lg bg-white p-5 shadow-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
        disabled || isApplying
          ? 'cursor-wait opacity-60'
          : 'cursor-pointer hover:bg-neutral-25',
        className,
      )}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-busy={isApplying}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (disabled || isApplying) return;
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      {isApplying ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
          <Spinner className="h-6 w-6" />
        </div>
      ) : null}
      <div className="flex flex-col gap-y-1.5">
        <div className="flex items-start justify-between gap-x-2">
          <div className="text-body-sm flex-1 font-medium leading-tight text-neutral-150 line-clamp-2">
            {followUpTemplate.name}
          </div>
          {showActions &&
            onRenameFollowUpTemplate &&
            onDuplicateFollowUpTemplate &&
            onDeleteFollowUpTemplate && (
              <FollowUpTemplateCardActionButton
                followUpTemplate={followUpTemplate}
                onClickRenameTemplate={onRenameFollowUpTemplate}
                onClickDuplicateTemplate={onDuplicateFollowUpTemplate}
                onClickDeleteTemplate={onDeleteFollowUpTemplate}
                renameActionLabel={renameActionLabel}
              />
            )}
        </div>

        {followUpTemplate.subject && (
          <div className="text-caption text-neutral-110 line-clamp-1">
            <strong>Subject:</strong> {followUpTemplate.subject}
          </div>
        )}
      </div>

      {(hasVoiceNote ||
        hasBodyMessage ||
        followUpTemplate.activityTemplates.length > 0 ||
        isUsedInAutomation) && (
        <div className="flex flex-col gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {hasVoiceNote && followUpTemplate.providerVoiceNote && (
              <VoiceNoteLabel
                duration={followUpTemplate.providerVoiceNote.duration}
              />
            )}

            {hasBodyMessage && (
              <div
                className="flex items-center gap-x-1 rounded-full bg-neutral-50 px-2 py-1"
                aria-label="Includes a message"
                title="Includes a message"
              >
                <DocumentTextIcon className="h-4 w-4 text-neutral-150" />
                <span className="text-small-caption font-medium text-neutral-150">
                  Message
                </span>
              </div>
            )}

            <TotalAttachmentsCountBadge
              count={followUpTemplate.activityTemplates.length}
            />

            {isUsedInAutomation && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-25/30 px-2 py-1 text-small-caption font-medium text-orange-125">
                <SparklesIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Used in Automations
              </span>
            )}
          </div>

          {followUpTemplate.activityTemplates.length > 0 && (
            <ActivityTypeBadges
              activityTemplates={followUpTemplate.activityTemplates}
            />
          )}
        </div>
      )}

      <div className="flex flex-col gap-y-0.5">
        <div className="text-small-caption font-light text-neutral-110">
          <strong>Created:</strong> {createdTime}
          {wasUpdated && (
            <>
              {' | '}
              <strong>Updated:</strong> {updatedTime}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowUpTemplateCard;
